"use client";

import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Upload, Loader2, X, Sparkles, Check, Download, RefreshCw, Camera, Settings2, ChevronDown } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { showInsufficientCreditsToast } from "@/lib/insufficient-credits";
import { useAuth } from "@/components/auth-provider";
import { useAuthModal } from "@/store/auth-modal-store";
import { useUserStore } from "@/store/user-store";
import { supabase } from "@/lib/supabase";
import { MediaSelector } from "@/components/media-selector";
import { cn } from "@/lib/utils";
import { createGenerationTask, fetchGenerations } from "@/lib/api";
import { motion } from "framer-motion";
import { GenerationSource } from "@/lib/constants";
import { usePricingQuote } from "@/hooks/use-pricing-quote";
import { useGenerationPolling } from "@/hooks/use-generation-polling";
import { useMemo } from "react";
import { useGenerationStore } from "@/store/generation-store";
import { usePresetStore } from "@/store/preset-store";
import { GenerationTimer } from "@/components/generation/generation-timer";
import { showGenerationStartedToast } from "@/lib/generation-started-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { StepBadges } from "@/components/ui/step-badges";

// Outfit shape used by this view — historically came from a 5273-line
// hardcoded data.ts which has now been retired in favour of the DB-managed
// `presets` table (app=virtual-try-on). Mapping happens at fetch time below.
type Outfit = {
  id: string;
  display: string;
  remote: string;
  category: string;
  label: string;
  gender: string;
};

// --- Constants & Data ---

const STYLES = [
  { value: "virtual-try-on", label: "Виртуальная примерка (сохранить фон)" },
  { value: "studio", label: "Студийная съемка" },
  { value: "indoor", label: "В помещении" },
  { value: "outdoor", label: "На улице" }
];

const POSES = [
  { value: "auto", label: "Авто (Лучше для одежды)" },
  { value: "sitting", label: "Сидя" },
  { value: "standing", label: "Стоя" },
  { value: "side-profile", label: "Профиль" }
];

const PLANS = [
  { value: "auto", label: "Авто (Лучше для одежды)" },
  { value: "headshot", label: "Крупный план (по плечи)" },
  { value: "medium-shot", label: "Средний план (по пояс)" },
  { value: "wide-shot", label: "Общий план (в полный рост)" }
];

const RATIOS = [
  { value: "auto", label: "Авто" },
  { value: "16:9", label: "16:9" },
  { value: "9:16", label: "9:16" },
  { value: "1:1", label: "1:1" }
];


const OUTFIT_CATEGORIES = [
  { id: "all", label: "Все" },
  { id: "suits", label: "Костюмы" },
  { id: "dresses", label: "Платья" },
  { id: "elegant", label: "Элегантное" },
  { id: "casual", label: "Кэжуал" },
  { id: "t-shirts", label: "Футболки" },
  { id: "hoodies", label: "Худи" },
  { id: "pants", label: "Брюки" },
  { id: "sport", label: "Спорт" },
  { id: "streetwear", label: "Стритвир" },
  { id: "shoes", label: "Обувь" }
];

interface VirtualTryOnViewProps {
    isModal?: boolean;
    onClose?: () => void;
}

export function VirtualTryOnView({ isModal = false, onClose }: VirtualTryOnViewProps) {
  const { user } = useAuth();
  const openAuthModal = useAuthModal((s) => s.open);
  const { credits } = useUserStore();

  // Preset deep-link: pre-fill garment from shared link
  const pendingPresetRef = useGenerationStore((s) => s.pendingPresetRef);
  const setPendingPresetRef = useGenerationStore((s) => s.setPendingPresetRef);

  // State
  const [humanImage, setHumanImage] = useState<{ display: string, api: string } | null>(null);
  const [garmentImages, setGarmentImages] = useState<{ display: string, api: string }[]>(
    pendingPresetRef ? [{ display: pendingPresetRef, api: pendingPresetRef }] : []
  );
  useEffect(() => {
    if (pendingPresetRef) {
      setGarmentImages([{ display: pendingPresetRef, api: pendingPresetRef }]);
      setPendingPresetRef(null);
    }
  }, [pendingPresetRef, setPendingPresetRef]);
  const [selectedOutfitId, setSelectedOutfitId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [gender, setGender] = useState<"men" | "women">("men");
  const [taskId, setTaskId] = useState<string | null>(null);

  // Outfits — now sourced from the DB-managed `presets` table. The `display`
  // (thumbnail) and `remote` (URL sent to the provider) used to be different
  // (local sample vs. R2 cloud); after the wardrobe migration to Supabase
  // both point at the same `media_url`.
  const { fetchByApp, getByApp } = usePresetStore();
  useEffect(() => {
    fetchByApp("virtual-try-on");
  }, [fetchByApp]);
  const dbPresets = getByApp("virtual-try-on");
  const OUTFITS: Outfit[] = useMemo(() => {
    return dbPresets
      .filter(p => p.media_url)
      .map(p => {
        const url = p.media_url!;
        const presetGender = (p.defaults as { gender?: string } | undefined)?.gender
          // Fallback: derive from id prefix (legacy rows may not have it set).
          || (p.id.startsWith("male-") ? "men" : p.id.startsWith("female-") ? "women" : "women");
        return {
          id: p.id,
          display: url,
          remote: url,
          category: p.category || "all",
          label: p.title,
          gender: presetGender,
        };
      });
  }, [dbPresets]);
  
  const [settings, setSettings] = useState({
    style: "virtual-try-on",
    pose: "auto",
    plan: "auto",
    ratio: "auto",
    outputType: "image"
  });

  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const [activeUploadType, setActiveUploadType] = useState<"human" | "garment">("human");
  const step2Ref = useRef<HTMLDivElement>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null);
  const [result, setResult] = useState<{ image?: string; video?: string } | null>(null);

  // --- Helpers ---
  const parseResultUrl = (resultUrl: string) => {
      let imageUrl: string | undefined;
      let videoUrl: string | undefined;

      // Check if JSON list (multiple outputs)
      if (resultUrl.trim().startsWith('[') && resultUrl.trim().endsWith(']')) {
          try {
              const urls = JSON.parse(resultUrl);
              if (Array.isArray(urls)) {
                  urls.forEach((url: string) => {
                      const lower = url.toLowerCase();
                      if (lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm')) {
                          videoUrl = url;
                      } else {
                          imageUrl = url;
                      }
                  });
              }
          } catch (e) {
              // Result URL parse failed — fallback to single URL below
          }
      }

      // Fallback to single URL
      if (!imageUrl && !videoUrl) {
          const isVideo = resultUrl.toLowerCase().endsWith('.mp4') || 
                         resultUrl.toLowerCase().endsWith('.mov') || 
                         resultUrl.toLowerCase().endsWith('.webm');
          if (isVideo) videoUrl = resultUrl;
          else imageUrl = resultUrl;
      }

      return { image: imageUrl, video: videoUrl };
  };

  // --- Initialization Logic ---
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const loadLastGeneration = async () => {
      if (isInitialized || !user) return;
      
      try {
        const response = await fetchGenerations({
            limit: 5,
            sources: [GenerationSource.VIRTUAL_TRY_ON]
        });

        const lastSuccess = response.items.find((g: { status: string }) => g.status === 'COMPLETED');

        if (lastSuccess && lastSuccess.params) {
            // Restore Settings only — never auto-fill previously uploaded photos
            setSettings({
                style: lastSuccess.params.style || "virtual-try-on",
                pose: lastSuccess.params.pose || "auto",
                plan: lastSuccess.params.plan || "auto",
                ratio: lastSuccess.params.ratio || "auto",
                // Legacy "both" generations remap to "image" — that
                // option is no longer offered (one or the other; users
                // run two generations if they want both).
                outputType: lastSuccess.params.output_type === "video" ? "video" : "image"
            });
        }
      } catch (error) {
        // Session restore failed — start fresh
      } finally {
        setIsInitialized(true);
      }
    };

    loadLastGeneration();
  }, [user, isInitialized]);

  // --- Handlers ---

  const handleMediaSelect = (url: string) => {
    if (activeUploadType === "garment") {
      if (garmentImages.length >= 13) {
        toast.error("Максимум 13 изображений");
        return;
      }
      setGarmentImages(prev => [...prev, { display: url, api: url }]);
    } else {
      setHumanImage({ display: url, api: url });
      // Auto-scroll to step 2 on mobile after uploading photo
      setTimeout(() => {
        step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
    setIsMediaSelectorOpen(false);
  };

  const removeGarmentImage = (index: number) => {
    setGarmentImages(prev => prev.filter((_, i) => i !== index));
  };

  const openMediaSelector = (type: "human" | "garment") => {
    setActiveUploadType(type);
    setIsMediaSelectorOpen(true);
  };

  const handleSelectOutfit = (outfit: Outfit) => {
    setSelectedOutfitId(outfit.id);
    // Correctly map {local, remote} to {display, api}
    setGarmentImages([{ display: outfit.display, api: outfit.remote }]);
    toast.success(`Выбрано: ${outfit.label}`);
  };


  const pricingPayload = useMemo(() => {
    return { kind: 'image' as const, model_id: 'wiro-virtual-try-on-v2', params: { output_type: settings.outputType } };
  }, [settings.outputType]);
  const { quote: priceQuote } = usePricingQuote(pricingPayload);
  const cost = priceQuote?.user_price_credits ?? null;

  const handleGenerate = async () => {
    if (!user) {
      openAuthModal({ message: "Войдите, чтобы примерить одежду" });
      return;
    }
    if (!humanImage || garmentImages.length === 0) {
      toast.error("Загрузите фото и выберите одежду");
      return;
    }
    if (cost != null && credits < cost) {
      showInsufficientCreditsToast();
      return;
    }

    setIsProcessing(true);
    setGenerationStartedAt(Date.now());
    setResult(null);

    const price = cost;

    try {
      const task = await createGenerationTask({
        model_id: "wiro-virtual-try-on-v2",
        prompt: "Примерка одежды",
        source: GenerationSource.VIRTUAL_TRY_ON,
        params: {
          input_image_human: humanImage.api,
          input_image_clothes: garmentImages.map(g => g.api),
          style: settings.style,
          pose: settings.pose,
          plan: settings.plan,
          ratio: settings.ratio,
          output_type: settings.outputType
        },
        user_price_credits: price ?? undefined,
      });

      setTaskId(task.id);
      showGenerationStartedToast();

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Не удалось начать генерацию";
      toast.error(message);
      setIsProcessing(false);
      setGenerationStartedAt(null);
    }
  };

  const [activeTab, setActiveTab] = useState<"image" | "video">("image");

  useGenerationPolling(taskId, {
    onComplete: (resultUrl, status) => {
      setIsProcessing(false);
      setGenerationStartedAt(null);
      setTaskId(null);

      // Prefer meta.all_outputs from the polling status — that's the
      // authoritative list for Wiro VTO «output_type=both» (returns
      // signed image + video URLs). Falls back to the legacy
      // JSON-array-in-result_url path for older / non-Wiro models.
      let parsed: { image?: string; video?: string } = {};
      const meta = (status?.meta || {}) as Record<string, unknown>;
      const outputs = meta.all_outputs;
      if (Array.isArray(outputs) && outputs.length > 0) {
          for (const u of outputs) {
              if (typeof u !== "string") continue;
              const lower = u.toLowerCase();
              if (lower.endsWith(".mp4") || lower.endsWith(".mov") || lower.endsWith(".webm")) {
                  parsed.video = u;
              } else {
                  parsed.image = u;
              }
          }
      }
      if (!parsed.image && !parsed.video) {
          parsed = parseResultUrl(resultUrl);
      }

      setResult(parsed);
      if (parsed.video && !parsed.image) setActiveTab("video");
      else setActiveTab("image");
      toast.success("Образ готов!");
    },
    onFailed: () => {
      setIsProcessing(false);
      setGenerationStartedAt(null);
      setTaskId(null);
      toast.error("Генерация не удалась");
    },
    onTimeout: () => {
      setIsProcessing(false);
      setGenerationStartedAt(null);
      setTaskId(null);
      toast.error("Превышено время ожидания. Попробуйте снова.");
    },
    intervalMs: 2000,
  });

  const availableCategories = OUTFIT_CATEGORIES.filter(cat => {
    if (cat.id === "all") return true;
    return OUTFITS.some(o => o.gender === gender && o.category === cat.id);
  });

  useEffect(() => {
    const isAvailable = availableCategories.some(c => c.id === selectedCategory);
    if (!isAvailable) {
      setSelectedCategory("all");
    }
  }, [gender]);

  const filteredOutfits = OUTFITS.filter(o => {
    const matchCategory = selectedCategory === "all" || o.category === selectedCategory;
    const matchGender = o.gender === gender;
    return matchCategory && matchGender;
  });

  return (
    <div className={cn("flex flex-col bg-background text-foreground font-sans selection:bg-cyan-500/30 overflow-hidden relative", isModal ? "h-full" : "h-dvh")}>
      
      {isModal && onClose && (
        <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose} 
            className="absolute top-3 right-3 z-50 rounded-full bg-background/60 hover:bg-background/90 border border-border/30 h-9 w-9 text-muted-foreground hover:text-foreground transition-colors"
        >
            <X className="w-4 h-4" />
        </Button>
      )}

      {/* Header */}

      {/* Mobile-only step progress (parity with miniapp). Hides on lg+
          where the two-panel layout already gives spatial orientation. */}
      <StepBadges
        className="lg:hidden px-3 py-2 border-b border-border/40 shrink-0"
        steps={[
          { n: 1, label: "Загрузить фото" },
          { n: 2, label: "Выбрать одежду" },
          { n: 3, label: "Результат" },
        ]}
        currentStep={result ? 3 : (humanImage ? 2 : 1)}
      />

      {/* Main Split Layout */}
      <main className="flex-1 flex flex-col lg:flex-row relative overflow-y-auto lg:overflow-hidden min-h-0">

        {/* Left Panel - 45% */}
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="order-1 w-full shrink-0 lg:shrink lg:min-h-0 h-auto lg:h-full lg:w-[45%] xl:w-[45%] bg-muted/5 border-b lg:border-b-0 lg:border-r border-border relative flex flex-col p-4 lg:p-6 overflow-y-auto scrollbar-hide"
        >
           <div className={cn("flex items-center justify-between mb-3 lg:mb-6 shrink-0 h-10", isModal && "pr-10 lg:pr-0")}>
             <div className="flex items-center gap-2.5">
                 {/* DashboardHeader provides the mobile back-arrow. */}
                 <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">1</div>
                 <h2 className="text-sm font-semibold text-foreground tracking-tight lg:font-bold lg:uppercase lg:tracking-wider">Ваше фото</h2>
             </div>
             {humanImage && !result && (
               <Button
                 variant="ghost"
                 size="sm"
                 onClick={() => setHumanImage(null)}
                 className="text-xs text-muted-foreground hover:text-destructive h-auto p-0 hover:bg-transparent"
               >
                 Сбросить
               </Button>
             )}
           </div>

           <div className="flex-1 relative rounded-2xl overflow-hidden bg-background border border-border/50 shadow-sm group min-h-[140px] lg:min-h-[300px] hover:border-primary/20 transition-colors">
              {isProcessing ? (
                // Processing State
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 z-20 backdrop-blur-sm">
                   <div className="relative mb-6">
                     <div className="w-20 h-20 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
                     <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-primary animate-pulse" />
                   </div>
                   <p className="text-primary font-medium tracking-wide animate-pulse">Создаем ваш образ...</p>
                </div>
              ) : null}

              {result ? (
                // Result State
                <div className="relative w-full h-full flex items-center justify-center bg-black/5">
                  
                  {/* Tab Switcher (if both present) */}
                  {result.image && result.video && (
                    <div className="absolute top-4 z-20 flex bg-background/80 backdrop-blur rounded-full p-1 border border-border shadow-lg">
                      <button 
                         onClick={() => setActiveTab("image")}
                         className={cn(
                           "px-4 py-1.5 rounded-full text-xs font-bold transition-all uppercase tracking-wider", 
                           activeTab === "image" ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:text-foreground"
                         )}
                      >
                          Фото
                      </button>
                      <button 
                         onClick={() => setActiveTab("video")}
                         className={cn(
                           "px-4 py-1.5 rounded-full text-xs font-bold transition-all uppercase tracking-wider", 
                           activeTab === "video" ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:text-foreground"
                         )}
                      >
                          Видео
                      </button>
                    </div>
                  )}

                  {/* Content */}
                  {(activeTab === "video" && result.video) ? (
                    <video 
                      key={result.video}
                      src={result.video} 
                      autoPlay 
                      loop 
                      muted 
                      controls
                      className="w-full h-full object-contain max-h-[80vh]" 
                    />
                  ) : (
                    <Image 
                      key={result.image || "res-img"}
                      src={result.image || result.video!} 
                      alt="Result" 
                      fill 
                      className="object-contain" 
                    />
                  )}
                  
                  {/* Result Actions Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background/90 via-background/60 to-transparent flex gap-3 z-10 pt-20">
                    <Button 
                      className="flex-1 font-bold h-12 rounded-xl uppercase tracking-wider shadow-lg shadow-primary/20"
                      onClick={() => window.open(activeTab === "video" ? result.video : result.image, '_blank')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Скачать
                    </Button>
                    <Button 
                      variant="outline" 
                      className="bg-background/70 border-border/60 hover:bg-background h-12 rounded-xl uppercase tracking-wider backdrop-blur-sm w-12 p-0 shrink-0"
                      onClick={() => setResult(null)}
                      title="Попробовать еще"
                    >
                      <RefreshCw className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <div className="absolute top-4 left-4 z-10">
                    <Badge className="bg-primary text-primary-foreground font-bold border-0 px-3 py-1 shadow-lg shadow-primary/20">
                      РЕЗУЛЬТАТ
                    </Badge>
                  </div>
                </div>
              ) : humanImage ? (
                // Human Image Loaded State
                <div className="relative w-full h-full bg-muted/10">
                  <Image src={humanImage.display} alt="Model" fill className="object-contain opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent pointer-events-none" />
                  
                  {/* Quick Change Button */}
                  <Button 
                     variant="secondary" 
                     size="sm"
                     onClick={() => openMediaSelector("human")}
                     className="absolute bottom-4 right-4 shadow-lg backdrop-blur-sm bg-background/80 hover:bg-background"
                  >
                    <RefreshCw className="w-3 h-3 mr-2" />
                    Заменить фото
                  </Button>
                </div>
              ) : (
                // Empty State / Upload
                <div className="absolute inset-0 flex flex-col p-3 lg:p-8 overflow-y-auto scrollbar-hide">
                  <div
                    onClick={() => openMediaSelector("human")}
                    className="flex-1 min-h-[140px] lg:min-h-[240px] cursor-pointer hover:bg-muted/50 transition-all duration-300 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:shadow-lg hover:-translate-y-1 group bg-muted/5"
                  >
                    <div className="w-14 h-14 lg:w-24 lg:h-24 rounded-full bg-background border-2 border-border flex items-center justify-center mb-2 lg:mb-6 group-hover:scale-110 group-hover:border-primary/50 transition-all duration-300 shadow-sm">
                        <Camera className="w-6 h-6 lg:w-10 lg:h-10 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <h3 className="text-base lg:text-2xl font-bold text-foreground mb-0.5 lg:mb-3 group-hover:text-primary transition-colors">Загрузить фото</h3>
                    <p className="text-muted-foreground text-xs lg:text-sm max-w-xs text-center leading-relaxed px-4">
                        Фото в полный рост или по пояс
                    </p>
                  </div>

                </div>
              )}
           </div>

        </motion.div>

        {/* Right Panel - 55% — step-based rendering on mobile (parity with
             miniapp): only render the outfit grid AFTER the user has
             attached a photo. On lg+ we always render both panels side-
             by-side because horizontal real estate makes the dual layout
             feel like a control surface rather than a stacked form. */}
        <motion.div
            ref={step2Ref}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
            className={cn(
                "order-2 w-full h-[70dvh] lg:h-full lg:min-h-0 flex-1 lg:w-[55%] xl:w-[55%] flex-col bg-background relative z-10",
                humanImage ? "flex" : "hidden lg:flex",
            )}
        >
           {/* Fixed Header Section */}
           <div className={cn("px-4 lg:px-6 pt-4 lg:pt-6 pb-2 bg-background z-20 shrink-0 border-b border-transparent", isModal && "lg:pr-14")}>
              <div className="flex flex-col gap-3 lg:gap-4">
                {/* Header & Gender Tabs */}
                <div className="flex items-center justify-between shrink-0 h-10 gap-2">
                   <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">2</div>
                      <h2 className="text-sm font-semibold text-foreground tracking-tight lg:font-bold lg:uppercase lg:tracking-wider truncate">Выберите одежду</h2>
                   </div>

                   <div className="flex bg-muted/50 p-1 rounded-lg shrink-0">
                      <button
                        onClick={() => setGender("women")}
                        className={cn(
                          "px-3 lg:px-4 py-1.5 text-xs font-medium rounded-md transition-all",
                          gender === "women" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        Женский
                      </button>
                      <button
                        onClick={() => setGender("men")}
                        className={cn(
                          "px-3 lg:px-4 py-1.5 text-xs font-medium rounded-md transition-all",
                          gender === "men" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        Мужской
                      </button>
                   </div>
                </div>

                {/* Categories */}
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-2 px-2 shrink-0">
                   {availableCategories.map(cat => (
                      <button 
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={cn(
                          "px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all border",
                          selectedCategory === cat.id ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"
                        )}
                      >
                         {cat.label}
                      </button>
                   ))}
                </div>
              </div>
           </div>

           {/* Scrollable Outfits Grid */}
           <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6 scrollbar-hide">
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 pt-2">
                   {/* Preset Outfits */}
                   {filteredOutfits.map(outfit => (
                      <div 
                        key={outfit.id} 
                        onClick={() => handleSelectOutfit(outfit)}
                        className={cn(
                          "aspect-[3/4] rounded-xl relative cursor-pointer overflow-hidden border-2 transition-all group",
                          selectedOutfitId === outfit.id ? "border-primary shadow-lg shadow-primary/10" : "border-transparent hover:border-primary/50"
                        )}
                      >
                        <Image src={outfit.display} alt={outfit.label} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                          <p className="text-white text-[10px] font-bold uppercase tracking-wider line-clamp-1">{outfit.label}</p>
                        </div>
                        {selectedOutfitId === outfit.id && (
                          <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1 shadow-lg animate-in zoom-in duration-300">
                            <Check className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                   ))}
                </div>
           </div>

           {/* Upload Section (inside Panel 02 — contextual) */}
           <div className="border-t border-border bg-muted/5 z-20 shrink-0 p-4">
              <div className="flex items-center justify-between mb-3">
                  <div>
                     <span className="block text-xs font-bold text-foreground">Своя одежда</span>
                     <span className="block text-[10px] text-muted-foreground mt-0.5">Загрузите фото своей одежды (до 13 шт)</span>
                  </div>
              </div>

              {/* Selected Custom Images Preview */}
              {garmentImages.length > 0 && !selectedOutfitId && (
                 <div className="flex gap-2 overflow-x-auto pb-3 mb-1 scrollbar-hide">
                     {garmentImages.map((img, idx) => (
                         <div key={`preview-${idx}`} className="relative w-14 h-16 rounded-lg overflow-hidden shrink-0 border border-primary/50 shadow-sm group">
                             <Image src={img.display} alt="Custom" fill className="object-cover" />
                             <button
                                onClick={(e) => { e.stopPropagation(); removeGarmentImage(idx); }}
                                className="absolute top-0.5 right-0.5 p-1 bg-black/60 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm"
                             >
                                <X className="w-2 h-2" />
                             </button>
                         </div>
                     ))}
                 </div>
              )}

              <Button
                 variant="outline"
                 onClick={() => openMediaSelector("garment")}
                 className={cn(
                     "w-full border-dashed border-2 h-10 text-xs font-medium uppercase tracking-wide hover:border-primary/70 hover:bg-primary/5 transition-all",
                     (!selectedOutfitId && garmentImages.length > 0) ? "border-primary/50 text-primary bg-primary/5" : "border-muted-foreground/30 text-muted-foreground hover:text-foreground"
                 )}
              >
                 <Upload className="mr-2 h-3 w-3" />
                 {garmentImages.length > 0 && !selectedOutfitId ? "Добавить еще фото" : "Загрузить фото"}
              </Button>
           </div>
        </motion.div>
      </main>

      {/* Settings — collapsed by default on mobile so the outfit grid is
          right under «Загрузить фото». 99% of users keep auto/auto/auto/auto/
          both and want a one-tap «Примерить»; advanced users open the
          drawer. lg+ keeps everything visible since horizontal real estate
          is plenty. */}
      {humanImage && !result && !isProcessing && (
        <div className="border-t border-border bg-muted/5 z-20 shrink-0 px-4 lg:px-6 lg:py-4">
          <Collapsible className="group lg:hidden">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between py-3 text-left outline-none">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground group-data-[state=open]:text-foreground transition-colors">
                  <Settings2 className="w-3.5 h-3.5" />
                  Доп. настройки
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground/60 transition-transform group-data-[state=open]:rotate-180" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down overflow-hidden">
              <div className="grid grid-cols-2 gap-3 pb-4 pt-1">
                {[
                  { key: 'style' as const, label: 'Стиль', options: STYLES, span: 'col-span-2' },
                  { key: 'pose' as const, label: 'Поза', options: POSES },
                  { key: 'plan' as const, label: 'План', options: PLANS },
                  { key: 'ratio' as const, label: 'Формат', options: RATIOS },
                  { key: 'outputType' as const, label: 'Тип вывода', options: [
                    { value: 'image', label: 'Фото' },
                    { value: 'video', label: 'Видео' },
                  ] },
                ].map(({ key, label, options, span }) => (
                  <div key={key} className={cn('space-y-1', span)}>
                    <Label className="text-[10px] uppercase text-muted-foreground ml-1">{label}</Label>
                    <Select value={settings[key]} onValueChange={(v) => setSettings(s => ({ ...s, [key]: v }))}>
                      <SelectTrigger className="bg-background border-border h-9 text-xs font-medium">
                        <SelectValue placeholder={label} />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Desktop / lg layout — flat 5-column grid (no collapse) */}
          <div className="hidden lg:grid grid-cols-5 gap-3">
            <div className="space-y-1 col-span-2">
              <Label className="text-[10px] uppercase text-muted-foreground ml-1">Стиль</Label>
              <Select value={settings.style} onValueChange={(v) => setSettings(s => ({ ...s, style: v }))}>
                <SelectTrigger className="bg-background border-border h-9 text-xs font-medium">
                  <SelectValue placeholder="Стиль" />
                </SelectTrigger>
                <SelectContent>
                  {STYLES.map(s => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground ml-1">Поза</Label>
              <Select value={settings.pose} onValueChange={(v) => setSettings(s => ({ ...s, pose: v }))}>
                <SelectTrigger className="bg-background border-border h-9 text-xs font-medium">
                  <SelectValue placeholder="Поза" />
                </SelectTrigger>
                <SelectContent>
                  {POSES.map(s => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground ml-1">План</Label>
              <Select value={settings.plan} onValueChange={(v) => setSettings(s => ({ ...s, plan: v }))}>
                <SelectTrigger className="bg-background border-border h-9 text-xs font-medium">
                  <SelectValue placeholder="План" />
                </SelectTrigger>
                <SelectContent>
                  {PLANS.map(s => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground ml-1">Формат</Label>
              <Select value={settings.ratio} onValueChange={(v) => setSettings(s => ({ ...s, ratio: v }))}>
                <SelectTrigger className="bg-background border-border h-9 text-xs font-medium">
                  <SelectValue placeholder="Формат" />
                </SelectTrigger>
                <SelectContent>
                  {RATIOS.map(s => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground ml-1">Тип вывода</Label>
              <Select value={settings.outputType} onValueChange={(v) => setSettings(s => ({ ...s, outputType: v }))}>
                <SelectTrigger className="bg-background border-border h-9 text-xs font-medium">
                  <SelectValue placeholder="Тип вывода" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="image" className="text-xs">Фото</SelectItem>
                  <SelectItem value="video" className="text-xs">Видео</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Shared Footer - Generate Button */}
      <div className="border-t border-border bg-background/80 backdrop-blur-sm z-20 shrink-0 px-4 pt-2 pb-[calc(64px+env(safe-area-inset-bottom))] md:p-4 lg:p-6 lg:pb-6">
         <Button
             onClick={handleGenerate}
             disabled={!humanImage || (!selectedOutfitId && garmentImages.length === 0) || isProcessing || cost == null}
             className={cn(
             "w-full h-12 lg:h-14 text-sm lg:text-base font-bold tracking-tight lg:uppercase lg:tracking-widest shadow-xl transition-all duration-500",
             (!humanImage || (!selectedOutfitId && garmentImages.length === 0))
                 ? "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
                 : "bg-primary hover:bg-primary/90 text-primary-foreground hover:scale-[1.02] hover:shadow-primary/25"
             )}
         >
             {isProcessing ? (
             <GenerationTimer startedAt={generationStartedAt} label="Примеряем" />
             ) : (
             <span className="flex items-center gap-2">
                 <Sparkles className="w-5 h-5" />
                 Примерить {user && cost != null ? `• ${cost}` : ''}
             </span>
             )}
         </Button>
         {!isProcessing && (!humanImage || (!selectedOutfitId && garmentImages.length === 0)) && (
           <p className="mt-2 text-center text-xs text-muted-foreground">
             {!humanImage ? 'Загрузите фото человека, чтобы продолжить' : 'Выберите готовый образ или загрузите вещь'}
           </p>
         )}
      </div>


      <MediaSelector
        open={isMediaSelectorOpen}
        onOpenChange={setIsMediaSelectorOpen}
        onSelect={handleMediaSelect}
        maxFiles={activeUploadType === "garment" ? 13 : 1}
      />
    </div>
  )
}
